import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal, untracked, viewChild } from '@angular/core';

import { IColumn } from '@app/shared/interceptors/column.interface';
import { SessionService } from '@app/shared/services/session.service';
import { NO_ACTIVITY_DAYS } from '@app/shared/constants/global.constant';
import { ISessionResponse } from '@app/shared/interfaces/session.interface';
import { TableComponent } from '@shared/components/table-component/table.component';
import { CounterDetailComponent } from '../counter-detail/counter-detail.component';
import { ITableChangeEvent } from '@app/shared/interfaces/table-change-event.interface';

import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-online-counters-table',
  imports: [CommonModule, TableComponent, ButtonModule, TooltipModule, CounterDetailComponent],
  templateUrl: './online-counters-table.component.html',
})
export class OnlineCountersTableComponent {
  selectedPort = input<string>('ALL');
  sessionService = inject(SessionService);

  tableComponent = viewChild.required(TableComponent);
  counterDetailDialog = viewChild.required(CounterDetailComponent);
  nameColumnTemplate = viewChild('nameColumnTemplate');
  statusColumnTemplate = viewChild('statusColumnTemplate');
  detailColumnTemplate = viewChild('detailColumnTemplate');
  loginDateTimeColumnTemplate = viewChild('loginDateTimeColumnTemplate');
  appTypeColumnTemplate = viewChild.required('appTypeColumnTemplate');

  sessionServiceRespone = signal<any>(null);
  onlineCounterData = signal<any[]>([]);
  tableLoading = signal<boolean>(false);
  searchFilterValue = signal<string>('');

  cols: IColumn[] = [];
  noActivityDays = NO_ACTIVITY_DAYS;

  constructor() {
    effect(() => {
      const port = this.selectedPort(); // Track changes to the selected port
      this.initTableCols();
      untracked(() => this.getSessionsForActiveCounters());
    });
  }

  // --- Initialize Operations ---
  initTableCols() {
    this.cols = [
      { key: 'computerName', label: 'Counter Name', template: this.nameColumnTemplate() },
      ...(this.selectedPort() === 'ALL' ? [{ key: 'defPort', label: 'Def Port' }] : []),
      {
        key: 'applicationType',
        label: 'App Type',
        searchable: true,
        template: this.appTypeColumnTemplate(),
      },
      { key: 'checkInSystem', label: 'Check-in System', searchable: true },
      {
        key: 'loginTime',
        label: 'Login Date | Time',
        template: this.loginDateTimeColumnTemplate(),
      },
      { key: 'status', label: 'Status', template: this.statusColumnTemplate() },
      { key: 'detail', label: '', template: this.detailColumnTemplate() },
    ];
  }

  // --- API Operation ---
  getSessionsForActiveCounters() {
    const rowSize = this.tableComponent().rowsPerPage();
    const currentPage = this.tableComponent().currentPage() - 1;
    const computerName = this.searchFilterValue() ?? '';
    const defPort = this.selectedPort() === 'ALL' ? undefined : this.selectedPort();

    this.tableLoading.set(true);

    this.sessionService
      .getSessionsForActiveCounters(currentPage, rowSize, computerName, defPort)
      .subscribe({
        next: (response: ISessionResponse) => {
          const onlineCounterData = response.content;
          this.sessionServiceRespone.set(response);
          this.onlineCounterData.set(onlineCounterData);
          this.tableLoading.set(false);
        },
        error: (error) => {
          console.log(error);
          this.tableLoading.set(false);
        },
      });
  }

  // --- Filter and Pagination Operation ---
  onChangePage(event: ITableChangeEvent) {
    this.getSessionsForActiveCounters();
  }

  onSearchFilter(event: any) {
    this.searchFilterValue.set(event);
    this.tableComponent().currentPage.set(1);
    this.getSessionsForActiveCounters();
  }

  openDetailDialog(data: any) {
    this.counterDetailDialog().displayDialog.set(true);
    this.counterDetailDialog().selectedItem.set(data);
  }
}
